import { getHeaders, PURCHASER_API_BASE_URL } from "../Constants";
import type { FileUpload } from "./FileUpload";

export enum AttributeType {
    STRING = "STRING",
    FILE = "FILE",
    NUMBER = "NUMBER",
    DATE = "DATE",
    PHONE = "PHONE",
    SELECT = "SELECT",
    MULTI_SELECT = "MULTI_SELECT",
    PRICE = "PRICE",
    ADDRESS = "ADDRESS",
    BOOLEAN = "BOOLEAN",
}

// One choice in a SELECT / MULTI_SELECT definition. `id` is server-assigned.
export interface AttributeDefinitionOption {
    id: number;
    value: string;
}

// Used when saving a definition. Options without `id` are inserted; options with
// an `id` reference an existing option (and may rename its `value`). Options
// absent from the list on update are deleted (cascades any entity values).
export interface AttributeDefinitionOptionInput {
    id?: number;
    value: string;
}

export interface AttributeDefinition {
    id: number;
    type: AttributeType;
    name: string;
    description: string;
    // TODO: Update API to return this value instead for being generic?
    // entityCount: number;
    weight: number;
    // Populated only for SELECT / MULTI_SELECT — the ordered list of choices.
    options?: AttributeDefinitionOption[];
}

export interface ContactAttribute extends AttributeDefinition {
    contactCount: number;
}

export interface VendorAttribute extends AttributeDefinition {
    vendorCount: number;
}

export interface RFQAttribute extends AttributeDefinition {
    rfqCount: number;
}

interface BaseAttributeValue {
    attributeId: number;
    name: string;
    description?: string;
}

interface StringAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.STRING;
    value: string;
}

interface NumberAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.NUMBER;
    value: number;
}

interface FileAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.FILE;
    value: string;
    fileId: number;
    file?: FileUpload;
}

interface DateAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.DATE;
    // Epoch milliseconds.
    value: number;
}

interface PhoneAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.PHONE;
    // E.164 formatted number, e.g. "+14155551234". May be "" when unset.
    value: string;
    // ISO 3166-1 alpha-2 region code, e.g. "US". Drives formatting/validation.
    regionCode?: string;
    extension?: string;
}

interface SelectAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.SELECT;
    // The chosen option (null when unset).
    option: AttributeDefinitionOption | null;
    // The full list of available choices, carried alongside the value so the
    // input doesn't need separate access to the definition.
    possibleOptions: AttributeDefinitionOption[];
}

interface MultiSelectAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.MULTI_SELECT;
    // The chosen options.
    options: AttributeDefinitionOption[];
    // The full list of available choices, carried alongside the value so the
    // input doesn't need separate access to the definition.
    possibleOptions: AttributeDefinitionOption[];
}

interface PriceAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.PRICE;
    // The monetary amount (null when unset).
    value: number | null;
    // ISO 4217 currency code, e.g. "USD" (null when unset).
    currency: string | null;
}

interface AddressAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.ADDRESS;
    // Display string: Google's formatted address once validated, else the raw
    // user input. May be "" when unset.
    value: string;
    // True only when Google confirmed a complete, unambiguous address. A set but
    // unverified address (manual entry the backend couldn't resolve) stays false.
    complete: boolean;
    // Structured components, populated by the backend's Google validation
    // (undefined when not validated/resolved).
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    // State / province / region (Google "administrativeArea").
    state?: string;
    postalCode?: string;
    // ISO 3166-1 alpha-2 country code, e.g. "US" (Google "regionCode").
    country?: string;
    latitude?: number;
    longitude?: number;
    // Google Place ID for the validated location.
    placeId?: string;
}

interface BooleanAttributeValue extends BaseAttributeValue {
    type: typeof AttributeType.BOOLEAN;
    // The boolean value (null when unset).
    value: boolean | null;
}

export type AttributeValue =
    | StringAttributeValue
    | NumberAttributeValue
    | FileAttributeValue
    | DateAttributeValue
    | PhoneAttributeValue
    | SelectAttributeValue
    | MultiSelectAttributeValue
    | PriceAttributeValue
    | AddressAttributeValue
    | BooleanAttributeValue;

interface BaseAttributeValueInput {
    attributeId: number;
}

interface StringAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.STRING;
    value?: string | null;
}

interface NumberAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.NUMBER;
    value?: number | null;
}

interface FileAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.FILE;
    value?: string | null;
    fileId?: number | null;
}

interface DateAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.DATE;
    // Epoch milliseconds.
    value?: number | null;
}

interface PhoneAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.PHONE;
    // E.164 formatted number, e.g. "+14155551234".
    value?: string | null;
    regionCode?: string | null;
    extension?: string | null;
}

interface SelectAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.SELECT;
    // null clears the value.
    optionId?: number | null;
}

interface MultiSelectAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.MULTI_SELECT;
    // Empty array clears the value.
    optionIds?: number[];
}

interface PriceAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.PRICE;
    // The monetary amount; null clears the value.
    value?: number | null;
    // ISO 4217 currency code. Defaults to USD in the handler when omitted.
    currency?: string | null;
}

interface AddressAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.ADDRESS;
    // Display string; "" clears the value. The backend re-validates on save.
    value?: string | null;
    complete?: boolean | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    // ISO 3166-1 alpha-2 country code.
    country?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    placeId?: string | null;
}

interface BooleanAttributeValueInput extends BaseAttributeValueInput {
    type: typeof AttributeType.BOOLEAN;
    // The boolean value; null clears the value.
    value?: boolean | null;
}

export type AttributeValueInput =
    | StringAttributeValueInput
    | NumberAttributeValueInput
    | FileAttributeValueInput
    | DateAttributeValueInput
    | PhoneAttributeValueInput
    | SelectAttributeValueInput
    | MultiSelectAttributeValueInput
    | PriceAttributeValueInput
    | AddressAttributeValueInput
    | BooleanAttributeValueInput;

export enum AttributeEntityType {
    Contact = "Contact",
    Vendor = "Vendor",
    RFQ = "RFQ",
}

// The address components we send to the backend to validate. All optional — the
// user may have only filled some fields. `country` (ISO 3166-1 alpha-2) sharpens
// Google's validation when provided.
export interface AddressValidationInput {
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
}

// The backend's response after running the input through Google's Address
// Validation API. Mirrors the stored ADDRESS value shape so the UI can offer the
// normalized result for confirmation and persist it verbatim on accept.
export interface ValidatedAddress {
    // Google's formatted, single-line address.
    formatted: string;
    // True only when Google confirmed a complete, unambiguous address.
    complete: boolean;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    placeId?: string | null;
}

export default {
    // RFQ Attributes
    updateRFQAttributes: (rfqPublicId: string, data: { [key: string]: any }): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/attributes`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(data),
        }).then(res => res.json()),

    getRFQAttributes: (): Promise<{ attributes: RFQAttribute[] }> =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/attributes`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),
    addRFQAttribute: (rfqAttribute: {
        type: AttributeType;
        name: string;
        description: string | null;
        weight: number;
        options?: AttributeDefinitionOptionInput[];
    }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/attributes`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(rfqAttribute),
        }),
    updateRFQAttribute: (rfqAttribute: {
        id: number;
        type: AttributeType;
        name: string;
        description: string | null;
        weight: number;
        options?: AttributeDefinitionOptionInput[];
    }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/attributes/${rfqAttribute.id}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(rfqAttribute),
        }),
    deleteRFQAttribute: (rfqAttributeId: number): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/attributes/${rfqAttributeId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }),
    getRFQAttributeValues: (
        publicId: string,
    ): Promise<{
        attributeValues: AttributeValue[];
    }> => {
        /**
         * This method fetches all of the attribute values for a specific RFQ
         */
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${publicId}/attributes`, {
            method: "GET",
            headers: getHeaders(),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to fetch RFQ attribute values");
            }
            return res.json();
        });
    },
    /**
     * Updates one or more attribute values for a specific RFQ.
     * @param rfqUniquePublicId The unique public id of the RFQ
     * @param attributes Array of attribute value inputs ({ attributeId, type, value, ... })
     */
    updateRFQAttributeValues: (rfqUniquePublicId: string, attributes: AttributeValueInput[]): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqUniquePublicId}/attributes`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(attributes),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update RFQ attribute values");
            }
            return res.json();
        }),

    // Contact Attributes
    updateContactAttributeValues: (contactId: string, attributes: AttributeValueInput[]): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/${contactId}/attributes`, {
            headers: getHeaders(),
            method: "PUT",
            body: JSON.stringify(attributes),
        }).then(res => res.json()),
    getContactAttributes: (): Promise<{ attributes: ContactAttribute[] }> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/attributes`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),
    addContactAttribute: (contactAttribute: {
        type: AttributeType;
        name: string;
        description: string | null;
        weight: number;
        options?: AttributeDefinitionOptionInput[];
    }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/attributes`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(contactAttribute),
        }),
    updateContactAttribute: (contactAttribute: {
        id: number;
        type: AttributeType;
        name: string;
        description: string | null;
        weight: number;
        options?: AttributeDefinitionOptionInput[];
    }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/attributes/${contactAttribute.id}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(contactAttribute),
        }),
    deleteContactAttribute: (contactAttributeId: number): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/contacts/attributes/${contactAttributeId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }),

    // Vendor Attributes
    getVendorAttributes: (): Promise<{ attributes: VendorAttribute[] }> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/attributes`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),
    addVendorAttribute: (vendorAttribute: {
        type: AttributeType;
        name: string;
        description: string | null;
        weight: number;
        options?: AttributeDefinitionOptionInput[];
    }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/attributes`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(vendorAttribute),
        }),
    updateVendorAttribute: (vendorAttribute: {
        id: number;
        type: AttributeType;
        name: string;
        description: string | null;
        weight: number;
        options?: AttributeDefinitionOptionInput[];
    }): any =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/attributes/${vendorAttribute.id}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(vendorAttribute),
        }),
    deleteVendorAttribute: (contactAttributeId: number): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/attributes/${contactAttributeId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }),
    updateVendorAttributeValues: (vendorRelationshipId: number, attributes: AttributeValueInput[]): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/vendor-relationships/${vendorRelationshipId}/attributes`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(attributes),
        }).then(res => res.json()),

    // Runs an address through the backend's Google Address Validation pass and
    // returns the normalized result. The UI uses this to show "here's the
    // standardized version" for confirmation before storing an ADDRESS value.
    // Newer endpoint — not behind the /v2/ prefix the attribute routes use.
    validateAddress: (address: AddressValidationInput): Promise<{ address: ValidatedAddress }> =>
        fetch(`${PURCHASER_API_BASE_URL}/addresses/validate`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(address),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to validate address");
            }
            return res.json();
        }),

    // Shared
    getAttributeValues: async (entityType: AttributeEntityType, attributeId: number, search?: string): Promise<{ values: string[] }> => {
        const basePath =
            entityType === AttributeEntityType.Vendor
                ? "/vendor-relationships/attributes"
                : entityType === AttributeEntityType.RFQ
                  ? "/v2/rfqs/attributes"
                  : "/contacts/attributes";
        const searchParam = search ? `?search=${encodeURIComponent(search)}` : "";
        const res = await fetch(`${PURCHASER_API_BASE_URL}${basePath}/${attributeId}/values${searchParam}`, {
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get attribute values");
        }
        return await res.json();
    },
};
